import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Tooltip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Link,
  DialogActions,
  Autocomplete,
} from '@mui/material'
import {
  LocalHospital,
  Event,
  Person,
  CalendarMonth,
  InfoOutlined,
  Note,
  Info,
  Place,
  ContentCopy,
  DeleteOutline,
  ReceiptLong,
  RequestQuote,
  AccountBalance,
} from '@mui/icons-material'
import EditIcon from '@mui/icons-material/Edit'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DirectionsCar from '@mui/icons-material/DirectionsCar'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import moment from 'moment'
import API from '../../../services/API'
import MFALogin from '../../../helpers/auth/MFALogin'
import {
  QuoteStatusChip,
  useBookingQuoteStatuses,
} from '../components/quoteStatus'
import { riskFlagOptions } from '../NewPatient/clinicalRiskFlags'
import NewPTSBooking from '../NewBooking/NewBooking'
import BigModal from '../../../components/Modals/BigModal'
import AddNewPatient from '../NewPatient/AddNewPatient'
import { OUTSTANDING_CHIP_LABELS } from '../outstandingLabels'
import SmallModal from '../../../components/Modals/SmallModal'
import { DetailRow } from '../../../components/Cards/DetailsBox'
import { buildAddress } from '../buildAddress'
import { GlobalContext } from '../../../App'
import { regionLabels } from '../../../helpers/region'
import CancelJourney from './CancelJourney'
import BookingInvoices from './BookingInvoices'
import BookingQuotes from './BookingQuotes'
import JourneyTimesStrip from '../components/JourneyTimesStrip'

const Section = ({ title, icon, children, buttonRight, id }) => (
  <Paper
    id={id}
    elevation={0}
    sx={{
      p: { xs: 2, md: 3 },
      borderRadius: 3,
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        {icon && (
          <Avatar sx={{ bgcolor: 'anMainBlue.main', width: 30, height: 30 }}>
            {icon}
          </Avatar>
        )}
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Stack>
      {buttonRight && buttonRight}
    </Stack>
    {children}
  </Paper>
)

const LabelValue = ({ label, value, noValue, children, md }) => (
  <Grid item xs={12} md={md ?? 6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value ? value : noValue ? '' : '—'}
    </Typography>
    {children}
  </Grid>
)

const formatWardDepartment = (ward) =>
  [ward?.name || ward?.wardName, ward?.internalLocation || ward?.wardLocation]
    .filter(Boolean)
    .join(' - ')

const lockedJourneyStatuses = ['Completed', 'Cancelled', 'No Show', 'Aborted']

// Small admin edits (requesting ward, notes) that stay possible after a crew
// has the job — the full edit form is locked once a journey is allocated,
// but none of these fields move a stop or affect pricing.
const EditAdminDetailsDialog = ({ open, onClose, booking, onSaved }) => {
  const [wardOptions, setWardOptions] = useState([])
  const [ward, setWard] = useState(null)
  const [bookingNotes, setBookingNotes] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const orgId = booking?.organisation?._id || booking?.organisation
  const showWard = !booking?.patientIsCustomer && !!orgId

  useEffect(() => {
    if (!open) return
    setWard(booking?.wardDepartment || null)
    setBookingNotes(booking?.bookingNotes || '')
    setNote('')
    setError('')
  }, [open, booking])

  useEffect(() => {
    if (!open || !showWard) return
    API.get(`/pts/warddepartmentoptions/${orgId}`)
      .then((res) => {
        if (res.status === 200) setWardOptions(res.data || [])
      })
      .catch(() => {})
  }, [open, showWard, orgId])

  const handleSave = async () => {
    if (showWard && !(ward?._id || ward)) {
      setError('Select the requesting ward.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const res = await API.put(
        `/pts/bookings/${booking._id}/admin-details`,
        {
          ...(showWard ? { wardDepartment: ward?._id || ward } : {}),
          bookingNotes,
          note,
        },
        { skipGlobalErrorHandler: true },
      )
      if (res.status === 200) {
        onSaved()
        onClose()
      } else {
        setError(res.data?.error || 'Unable to save changes.')
      }
    } catch (e) {
      console.error(e)
      setError('Unable to save changes.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          <Alert severity="info">
            These details can be changed at any time — including after the
            journey has been allocated to a crew. The crew will see the
            updated ward on their job.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          {showWard && (
            <Autocomplete
              options={wardOptions}
              value={ward}
              autoSelect
              onChange={(event, value) => setWard(value)}
              getOptionLabel={(option) =>
                formatWardDepartment(option) || option?.name || ''
              }
              isOptionEqualToValue={(option, value) =>
                String(option?._id) === String(value?._id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Requesting ward" required />
              )}
            />
          )}
          <TextField
            label="Booking notes"
            multiline
            minRows={3}
            fullWidth
            value={bookingNotes}
            onChange={(e) => setBookingNotes(e.target.value)}
          />
          <TextField
            label="Add a note to the activity log (optional)"
            multiline
            minRows={2}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Scroll to an on-page section without touching the URL - hash hrefs go
// through the router as navigation and don't reliably scroll the app's
// nested scroll container
const scrollToSection = (sectionId) =>
  document
    .getElementById(sectionId)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })

const INVOICE_GROUPING_LABELS = {
  perJourney: 'One invoice per journey',
  perBooking: 'One invoice per booking',
  perPatient: 'One invoice per patient',
  consolidatedWeekly: 'Consolidated weekly',
  consolidatedMonthly: 'Consolidated monthly',
}

const SingleBooking = ({ passedBookingID }) => {
  const { tenantInfo } = useContext(GlobalContext)
  const { bookingID } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [requiresMFA, setRequiresMFA] = useState(false)
  const [booking, setBooking] = useState(null)
  const quoteStatuses = useBookingQuoteStatuses(
    booking?._id ? [booking._id] : [],
  )
  const bookingQuoteStatus = booking?._id
    ? quoteStatuses[String(booking._id)]
    : null
  const [statusAnchorEl, setStatusAnchorEl] = useState(null)
  const [openConfirmStatusChange, setOpenConfirmStatusChange] = useState(false)
  const [statusChangeError, setStatusChangeError] = useState('')
  const [cancellationReason, setCancellationReason] = useState('')
  const [editMode, setEditMode] = useState(false)
  const [editDetailsOpen, setEditDetailsOpen] = useState(false)
  const [editPatient, setEditPatient] = useState(false)
  const [openStatusInfoPanel, setOpenStatusInfoPanel] = useState(false)
  const [cancelJourney, setCancelJourney] = useState('')
  const [savingCrewReview, setSavingCrewReview] = useState(false)
  const navigate = useNavigate()

  // Crew-created bookings (self-dispatched duties) on a per-journey billed
  // duty carry a "needs review" marker until a co-ordinator signs them off
  const setCrewReviewed = async (reviewed) => {
    setSavingCrewReview(true)
    try {
      const res = await API.put(`/pts/bookings/${booking._id}/crew-review`, {
        reviewed,
      })
      if (res.status === 200) {
        setBooking((current) => ({ ...current, crewJob: res.data.crewJob }))
      }
    } finally {
      setSavingCrewReview(false)
    }
  }

  const loadBooking = () => {
    setLoading(true)
    API.get(`/pts/booking-info/${passedBookingID ?? bookingID}`)
      .then((res) => {
        if (res.status === 200) {
          const firstJourney = res?.data?.journeys?.[0] ?? {}
          const pickupDropoffData = {
            journeyType: firstJourney.journeyType,
            dropoff: firstJourney.dropoff,
            pickup: firstJourney.pickup,
          }
          const returnJourneyData = firstJourney?.returnJourney
            ? {
                required: true,
                pickup: {
                  when: {
                    patientReadyFrom:
                      res?.data?.journeys?.[1]?.pickup?.when
                        ?.patientReadyFrom || null,
                  },
                },
              }
            : null
          setBooking({
            ...res.data,
            ...pickupDropoffData,
            ...returnJourneyData,
          })
        } else if (res.data?.errorCode === 'notAuthorised') {
          setRequiresMFA(true)
        } else {
          setError(true)
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    loadBooking()
  }, [bookingID, passedBookingID])

  useEffect(() => {
    if (loading || !window.location.hash) return

    const element = document.getElementById(window.location.hash.slice(1))
    if (element) {
      window.requestAnimationFrame(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [loading])

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <LinearProgress />
      </Box>
    )

  if (requiresMFA)
    return (
      <MFALogin
        onSuccess={() => {
          setRequiresMFA(false)
          loadBooking()
        }}
      />
    )

  if (error || !booking)
    return <Alert severity="error">Failed to load booking.</Alert>

  const { patient, organisation, createdBy, createdAt } = booking

  const patientRiskFlags = booking?.patient?.riskFlags || []

  const categorisedPatientRisks = riskFlagOptions.reduce(
    (acc, { group, color, options }) => {
      const matches = patientRiskFlags.filter((flag) => options.includes(flag))
      if (matches.length > 0) {
        acc.push({ group, color, flags: matches })
      }
      return acc
    },
    [],
  )

  const handleStatusChange = ({ newStatus, confirmed, cancellationReason }) => {
    if (['Rejected', 'Cancelled'].includes(newStatus) && !confirmed) {
      setStatusAnchorEl(null)
      setStatusChangeError('')
      setOpenConfirmStatusChange(newStatus)
    } else {
      API.put('/pts/bookings/update-status', {
        bookingID,
        newStatus,
        cancellationReason,
      }).then((res) => {
        if (res.status === 200) {
          loadBooking()
          setOpenConfirmStatusChange(null)
          setStatusAnchorEl(null)
          setStatusChangeError('')
        } else {
          setStatusChangeError(
            res.data?.error || 'Unable to update the booking status.',
          )
        }
      })
    }
  }

  return (
    <Stack spacing={3}>
      {/* Top Summary Card */}
      <Card
        sx={{
          p: 3,
          borderRadius: 4,
          backgroundColor: 'primary.main',
          color: '#fff',
          boxShadow: 3,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack spacing={1}>
            <Typography variant="h5" fontWeight={700} color="white">
              {patient?.demographics?.firstName}{' '}
              {patient?.demographics?.lastName}
            </Typography>
            <Typography variant="body2" color="white">
              {patient?.demographics?.dob
                ? moment(patient.demographics.dob).format('DD/MM/YYYY')
                : 'DOB not recorded'}{' '}
              • {patient?.demographics?.gender}
            </Typography>
            <Typography variant="body2" color="white">
              {booking?.patientIsCustomer
                ? 'Patient is Customer'
                : organisation?.orgName || '—'}{' '}
              • {booking?.journeyType}
            </Typography>
            {booking?.eligibilityScreening?.completed && (
              <Chip
                label={`Eligibility: ${booking.eligibilityScreening.outcome}`}
                size="small"
                color={
                  booking.eligibilityScreening.outcome === 'Eligible'
                    ? 'success'
                    : 'warning'
                }
                sx={{ borderRadius: 1, alignSelf: 'flex-start' }}
              />
            )}
            <QuoteStatusChip
              status={bookingQuoteStatus}
              sx={{
                alignSelf: 'flex-start',
                height: 24,
                fontSize: '0.8125rem',
              }}
            />
          </Stack>

          <Stack
            spacing={1}
            alignItems={{
              xs: 'flex-start',
              md: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" gap={1} alignItems="center">
              <Tooltip title="Click to copy to Clipboard">
                <Button
                  variant="contained"
                  sx={{ cursor: 'copy' }}
                  onClick={() =>
                    navigator.clipboard.writeText(
                      booking?.bookingRef ?? 'No Ref',
                    )
                  }
                  endIcon={<ContentCopy fontSize="small" />}
                >
                  {booking?.bookingRef ?? 'No Ref'}
                </Button>
              </Tooltip>
              <Tooltip title="Change booking status">
                <Button
                  variant="contained"
                  endIcon={<ExpandMoreIcon />}
                  color="secondary"
                  sx={{
                    bgcolor:
                      booking.status === 'Scheduled'
                        ? 'rgba(255,255,255,0.2)'
                        : 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    borderRadius: 1,
                  }}
                  onClick={(e) => setStatusAnchorEl(e.currentTarget)}
                >
                  {booking.status}
                </Button>
              </Tooltip>
              <Menu
                anchorEl={statusAnchorEl}
                open={!!statusAnchorEl}
                onClose={() => setStatusAnchorEl(null)}
              >
                {[
                  'Pending',
                  'Confirmed',
                  'Scheduled',
                  'Cancelled',
                  'Rejected',
                ].map((status) => (
                  <MenuItem
                    key={status}
                    selected={booking.status === status}
                    disabled={booking.status === status}
                    onClick={() => handleStatusChange({ newStatus: status })}
                  >
                    {status}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem
                  onClick={() => {
                    setStatusAnchorEl(null)
                    setOpenStatusInfoPanel(true)
                  }}
                >
                  <Info fontSize="small" sx={{ mr: 1 }} /> What do these mean?
                </MenuItem>
              </Menu>
              {!['Cancelled', 'Rejected'].includes(booking.status) && (
                <Tooltip title="Edit booking">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setEditMode(true)}
                    sx={{
                      border: '1px solid #d0d7de',
                      bgcolor: 'white',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        color: 'white',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!['Cancelled', 'Rejected'].includes(booking.status) && (
                <Tooltip title="Edit details (ward & notes) — available even after crew allocation">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setEditDetailsOpen(true)}
                    sx={{
                      border: '1px solid #d0d7de',
                      bgcolor: 'white',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        color: 'white',
                      },
                    }}
                  >
                    <EditNoteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <BigModal open={editMode} onClose={() => setEditMode(false)}>
                <NewPTSBooking
                  existingBooking={booking}
                  setEditMode={setEditMode}
                  loadBooking={loadBooking}
                />
              </BigModal>
              <EditAdminDetailsDialog
                open={editDetailsOpen}
                onClose={() => setEditDetailsOpen(false)}
                booking={booking}
                onSaved={loadBooking}
              />
            </Stack>
            <Stack>
              <Typography variant="caption">
                Created by {createdBy?.firstName} {createdBy?.lastName} -
                {moment(createdAt).format(' DD/MM/YY [-] HH:mm')}
              </Typography>
            </Stack>
          </Stack>
          <BigModal
            open={openStatusInfoPanel}
            onClose={() => setOpenStatusInfoPanel(false)}
            autoWidth
          >
            <Stack spacing={0.5}>
              <DetailRow label="Pending">
                Submitted, awaiting Review or Triage
              </DetailRow>
              <DetailRow label="Confirmed">Approved and Valid</DetailRow>
              <DetailRow label="In Progress">First Journey Started</DetailRow>
              <DetailRow label="Completed">All Journeys Completed</DetailRow>
              <DetailRow label="Cancelled">
                Cancelled after Confirmation
              </DetailRow>
              <DetailRow label="Rejected">
                Rejected during Review or Triage
              </DetailRow>
              <Button
                size="small"
                color="anRed"
                fullWidth
                onClick={() => setOpenStatusInfoPanel(false)}
              >
                Close
              </Button>
            </Stack>
          </BigModal>
          <Dialog
            open={!!openConfirmStatusChange}
            onClose={() => setOpenConfirmStatusChange(null)}
          >
            <DialogTitle>Confirm {openConfirmStatusChange}</DialogTitle>
            <DialogContent>
              <Stack gap={1}>
                <Typography variant="body2">
                  Confirm you wish to change the status to{' '}
                  {openConfirmStatusChange}. This will cancel <b>ALL</b>{' '}
                  associated <b>future</b> Journeys.
                </Typography>
                <TextField
                  placeholder="Reason"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  fullWidth
                />
                {statusChangeError && (
                  <Alert severity="error">{statusChangeError}</Alert>
                )}
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  gap={1}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setCancellationReason('')
                      setOpenConfirmStatusChange(null)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() =>
                      handleStatusChange({
                        newStatus: openConfirmStatusChange,
                        confirmed: true,
                        cancellationReason,
                      })
                    }
                    disabled={!cancellationReason}
                  >
                    Confirm
                  </Button>
                </Stack>
              </Stack>
            </DialogContent>
          </Dialog>
        </Stack>
      </Card>

      {booking.source === 'Crew' && (
        <Alert
          severity={
            booking.crewJob?.reviewRequired && !booking.crewJob?.reviewedAt
              ? 'warning'
              : 'info'
          }
          variant="outlined"
          action={
            booking.crewJob?.reviewRequired ? (
              <Button
                size="small"
                color="inherit"
                disabled={savingCrewReview}
                onClick={() => setCrewReviewed(!booking.crewJob?.reviewedAt)}
              >
                {booking.crewJob?.reviewedAt ? 'Undo review' : 'Mark as reviewed'}
              </Button>
            ) : null
          }
        >
          <b>Added by the crew on their duty.</b>{' '}
          {!booking.crewJob?.reviewRequired
            ? 'The duty is billed as a whole, so this journey is not charged individually.'
            : booking.crewJob?.reviewedAt
              ? `Reviewed ${moment(booking.crewJob.reviewedAt).format('DD/MM/YYYY HH:mm')}.`
              : 'It will be priced per journey by the next billing run — check the patient, stops and times before it is invoiced.'}
        </Alert>
      )}

      {booking.outstanding?.overall?.length > 0 &&
        !['Completed', 'Cancelled', 'Rejected'].includes(booking.status) && (
          <Alert
            severity="warning"
            variant="outlined"
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => setEditPatient(true)}
              >
                Edit Patient
              </Button>
            }
          >
            <b>Details to confirm before the journey can be completed:</b>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1}>
              {booking.outstanding.overall.map((code) => (
                <Chip
                  key={code}
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={OUTSTANDING_CHIP_LABELS[code] || code}
                />
              ))}
            </Stack>
          </Alert>
        )}

      {/* Patient Information */}
      <AddNewPatient
        open={!!editPatient}
        existingPatient={patient}
        onClose={() => setEditPatient(false)}
        actionAfterSubmit={() => {
          setEditPatient(false)
          loadBooking()
        }}
      />
      <Section
        title="Patient Details"
        icon={<Person fontSize="small" />}
        buttonRight={
          <Button
            variant="contained"
            color="anBlue"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setEditPatient(true)}
          >
            Edit Patient
          </Button>
        }
      >
        <Grid container spacing={2}>
          <LabelValue
            label="Date of Birth"
            value={
              patient?.demographics?.dob &&
              moment(patient.demographics.dob).format('DD/MM/YYYY')
            }
          />
          <LabelValue
            label="Address"
            value={[
              patient?.demographics?.addressLine1,
              patient?.demographics?.addressLine2,
              patient?.demographics?.town,
              patient?.demographics?.city,
              patient.demographics?.postCode,
            ]
              .filter(Boolean)
              .join(', ')}
          />
          <LabelValue
            label={regionLabels(tenantInfo?.region).nationalIdLabel}
            value={patient?.demographics?.nhsNumber}
          />
          <LabelValue
            label="GP Surgery"
            value={patient?.demographics?.gpSurgery}
          />
          <LabelValue
            label="Phone"
            value={
              [
                patient?.demographics?.phoneNumber1,
                patient?.demographics?.phoneNumber2,
              ]
                .filter(Boolean)
                .join(', ') || 'Not Provided'
            }
          />
          <LabelValue label="Email" value={patient?.demographics?.email} />
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <LabelValue
            label="Next of Kin"
            value={
              `${
                patient?.demographics?.nokName
                  ? patient.demographics.nokName
                  : ''
              }` +
              `${
                patient?.demographics?.nokRelationship
                  ? ' (' + patient.demographics.nokRelationship + ')'
                  : ''
              }`
            }
          />
          <LabelValue
            label="Contact Number"
            value={patient?.demographics?.nokContactNumber}
          />
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <LabelValue label="Clinical Notes" value={patient?.clinicalNotes} />
          <LabelValue label="Mobility" value={patient?.mobility?.category} />
          <LabelValue
            label="Height"
            value={
              patient?.mobility?.heightCm
                ? `${patient.mobility.heightCm} cm`
                : undefined
            }
          />
          <LabelValue
            label="Weight"
            value={
              patient?.mobility?.weightKg
                ? `${patient.mobility.weightKg} kg`
                : undefined
            }
          />
          <LabelValue
            label="Aids"
            value={patient?.mobility?.aids?.join(', ')}
          />
          <LabelValue
            label="Is Bariatric"
            value={patient?.mobility?.isBariatric ? 'Yes' : 'No'}
          />
          <LabelValue
            label="Must Travel Alone"
            value={patient?.mobility?.mustTravelAlone ? 'Yes' : 'No'}
          />
          {categorisedPatientRisks.map((x) => (
            <LabelValue key={x.group} label={x.group} noValue>
              <Stack
                direction="row"
                sx={{
                  flexWrap: 'wrap',
                  rowGap: 1,
                }}
                gap={1}
              >
                {(x.flags ?? []).map((o, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      backgroundColor: `${x.color}15`,
                      border: `1px solid ${x.color}40`,
                      borderRadius: '16px',
                      padding: '2px 8px',
                      color: x.color,
                      fontWeight: 500,
                      fontSize: '0.85rem',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: `${x.color}25`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: x.color,
                      }}
                    />
                    {o}
                  </Box>
                ))}
              </Stack>
            </LabelValue>
          ))}
        </Grid>
      </Section>

      {/* Organisation */}
      <Section
        title="Organisation & Requester"
        icon={<LocalHospital fontSize="small" />}
      >
        <Grid container spacing={2}>
          {booking?.patientIsCustomer ? (
            <Grid item xs={12}>
              <Alert severity="info" variant="outlined">
                This patient is being billed directly — see Billing Setup below.
              </Alert>
            </Grid>
          ) : (
            <>
              <LabelValue label="Organisation" value={organisation?.orgName} />
              <LabelValue
                label="Ward / Department"
                value={formatWardDepartment(booking?.wardDepartment)}
              />
              <LabelValue label="Requested By" noValue>
                {(booking.requestedBy || []).map((x, i) => (
                  <Typography key={i} variant="body2" fontWeight={500}>
                    {x.firstName || ''} {x.lastName || ''}{' '}
                    {x.companyRole ? `(${x.companyRole})` : ''}
                  </Typography>
                ))}
              </LabelValue>
              <LabelValue label="Source" value={booking.source} />
            </>
          )}
        </Grid>
      </Section>

      <Section
        id="billing-actions"
        title="Billing Setup"
        icon={<AccountBalance fontSize="small" />}
      >
        <Grid container spacing={2}>
          <LabelValue
            label="Customer"
            value={
              booking?.billingSetup?.customerType === 'Patient'
                ? `${patient?.demographics?.firstName || ''} ${patient?.demographics?.lastName || ''}`.trim() ||
                  'Patient'
                : organisation?.orgName
            }
          />
          <LabelValue
            label="Tariff in use"
            value={
              booking?.billingSetup?.tariff
                ? `${booking.billingSetup.tariff.name}${booking.billingSetup.tariff.isDefault ? ' (tenant default)' : ''}`
                : null
            }
          />
          {booking?.billingSetup?.customerType === 'Organisation' && (
            <LabelValue
              label="Invoice grouping"
              value={
                INVOICE_GROUPING_LABELS[
                  booking?.billingSetup?.invoiceGrouping
                ] || booking?.billingSetup?.invoiceGrouping
              }
            />
          )}
          {booking?.billingSetup?.requiresQuote && (
            <LabelValue label="Quote required" value="Yes, before planning" />
          )}
          {!booking?.billingSetup?.tariff && (
            <Grid item xs={12}>
              <Alert severity="warning" variant="outlined">
                No tariff resolves for this booking — quotes and auto-priced
                invoices won't be able to price it until a default tariff exists
                in{' '}
                <Link href="/site-configuration/pts-pricing">
                  Pricing &amp; Tariffs
                </Link>
                .
              </Alert>
            </Grid>
          )}
          {booking?.billingSetup?.customerType === 'Organisation' &&
            !booking?.billingSetup?.contractExists && (
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  No contract set up yet for{' '}
                  {organisation?.orgName || 'this organisation'} — using the
                  tenant default tariff and the '
                  {
                    INVOICE_GROUPING_LABELS[
                      booking?.billingSetup?.invoiceGrouping
                    ]
                  }
                  ' default. Set a contract-specific tariff, invoice grouping,
                  or SLA targets from the organisation's profile page.
                </Alert>
              </Grid>
            )}
        </Grid>
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mt: 2 }}
          action={
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={<RequestQuote />}
                onClick={() => scrollToSection('booking-quotes')}
              >
                Quote Tools
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ReceiptLong />}
                onClick={() => scrollToSection('booking-invoices')}
              >
                Invoice Tools
              </Button>
            </Stack>
          }
        >
          Quotes and invoices are managed below for this booking, including the
          generate quote and create invoice actions.
        </Alert>
      </Section>

      <Section
        id="booking-quotes"
        title="Quotes"
        icon={<RequestQuote fontSize="small" />}
      >
        <BookingQuotes booking={booking} />
      </Section>

      <Section
        id="booking-invoices"
        title="Invoices"
        icon={<ReceiptLong fontSize="small" />}
      >
        <BookingInvoices booking={booking} />
      </Section>

      {/* Pickup and Dropoff */}
      {/* <Section title="Pickup & Dropoff" icon={<Place fontSize="small" />}>
        <Grid container spacing={2}>
          <LabelValue label="Journey Type" value={booking?.journeyType} />
          {booking?.pickup?.when?.patientReadyFrom ? (
            <LabelValue
              label="Patient Ready From"
              value={moment(booking.pickup.when.patientReadyFrom).format(
                'Do MMM YYYY HH:mm',
              )}
            />
          ) : booking?.dropoff?.when?.targetArrivalTime ? (
            <LabelValue
              label="Target Arrival / Appointment Time"
              value={moment(booking.dropoff.when.targetArrivalTime).format(
                'Do MMM YYYY HH:mm',
              )}
            />
          ) : (
            <></>
          )}
        </Grid>
      </Section> */}

      {/* Journeys */}
      <Section title="Journeys" icon={<DirectionsCar fontSize="small" />}>
        {booking.journeys && booking.journeys.length > 0 ? (
          booking.journeys.map((j, i) => (
            <Accordion
              key={j._id}
              disableGutters
              sx={{
                mb: 1.5,
                borderRadius: 2,
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  borderRadius: 'inherit',
                  minHeight: 56,
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  gap={{ xs: 0.75, sm: 1 }}
                  width="100%"
                  minWidth={0}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexShrink={0}
                  >
                    <Chip
                      label={`Journey ${i + 1}`}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 600, borderRadius: 1, px: 1 }}
                    />
                    <Typography fontWeight={600}>
                      {j.journeyType || '—'}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    flexWrap="wrap"
                    rowGap={0.5}
                    minWidth={0}
                  >
                    <Typography variant="body2">
                      {j?.pickup?.when?.patientReadyFrom
                        ? `Patient Ready From: ${moment(
                            j.pickup.when.patientReadyFrom,
                          ).format('Do MMM YY - HH:mm')}`
                        : j?.dropoff?.when?.targetArrivalTime
                          ? `Target Arrival Time: ${moment(
                              j.dropoff.when.targetArrivalTime,
                            ).format('Do MMM YY - HH:mm')}`
                          : ''}
                    </Typography>
                    <Chip
                      label={j.status}
                      color={
                        j.status === 'Completed'
                          ? 'success'
                          : j.status === 'Planned'
                            ? 'warning'
                            : j.status === 'Rejected'
                              ? 'error'
                              : j.status === 'Cancelled'
                                ? 'error'
                                : 'default'
                      }
                      size="small"
                      variant="filled"
                      sx={{ borderRadius: 1 }}
                    />
                  </Stack>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ p: { xs: 1.5, md: 2 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} lg={4}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnIcon fontSize="small" color="action" />
                        <Stack minWidth={0}>
                          <Typography variant="body2">
                            <strong>Pickup:</strong> {j.pickup?.name ?? ''}
                          </Typography>
                          {formatWardDepartment({
                            wardName: j.pickup?.wardName,
                            wardLocation: j.pickup?.wardLocation,
                            ...j.pickup?.wardDepartmentAsAddress,
                          }) && (
                            <Typography variant="body2" color="text.secondary">
                              {formatWardDepartment({
                                wardName: j.pickup?.wardName,
                                wardLocation: j.pickup?.wardLocation,
                                ...j.pickup?.wardDepartmentAsAddress,
                              })}
                            </Typography>
                          )}
                          <Typography variant="body2" flexWrap="wrap">
                            {[
                              j.pickup?.addressLine1,
                              j.pickup?.addressLine2,
                              j.pickup?.town,
                              j.pickup?.city,
                              j.pickup?.postCode,
                            ]
                              .filter(Boolean)
                              .join(', ') || 'No pickup address'}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnIcon
                          fontSize="small"
                          color="action"
                          sx={{ transform: 'rotate(180deg)' }}
                        />
                        <Stack minWidth={0}>
                          <Typography variant="body2">
                            <strong>Dropoff:</strong> {j.dropoff?.name ?? ''}
                          </Typography>
                          {formatWardDepartment({
                            wardName: j.dropoff?.wardName,
                            wardLocation: j.dropoff?.wardLocation,
                            ...j.dropoff?.wardDepartmentAsAddress,
                          }) && (
                            <Typography variant="body2" color="text.secondary">
                              {formatWardDepartment({
                                wardName: j.dropoff?.wardName,
                                wardLocation: j.dropoff?.wardLocation,
                                ...j.dropoff?.wardDepartmentAsAddress,
                              })}
                            </Typography>
                          )}
                          <Typography variant="body2">
                            {[
                              j.dropoff?.addressLine1,
                              j.dropoff?.addressLine2,
                              j.dropoff?.town,
                              j.dropoff?.city,
                              j.dropoff?.postCode,
                            ]
                              .filter(Boolean)
                              .join(', ') || 'No dropoff address'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6} lg={4}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          <strong>Sequence (in booking):</strong> {j.sequence}
                        </Typography>
                      </Stack>
                      <Typography variant="body2">
                        <strong>Return Journey:</strong>{' '}
                        {j.returnJourney ? 'Yes (linked)' : 'No'}
                      </Typography>
                      <JourneyTimesStrip journey={j} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Stack spacing={1} alignItems="center">
                      {j?.ptsDuty?.start ? (
                        <Button
                          variant="contained"
                          onClick={() =>
                            window.open(
                              `/pts/planning?date=${moment(j.ptsDuty.start).format('YYYYMMDD')}`,
                              '_blank',
                            )
                          }
                          fullWidth
                        >
                          View Planning Page
                        </Button>
                      ) : (
                        <Stack justifyContent="center" alignItems="center">
                          <Typography>No Duty Set Yet</Typography>
                          <Button
                            variant="contained"
                            onClick={() =>
                              navigate(
                                `/pts/planning?date=${moment(j?.dropoff?.when?.targetArrivalTime ?? j?.pickup?.when?.patientReadyFrom ?? null).format('YYYYMMDD')}`,
                              )
                            }
                            fullWidth
                          >
                            View Planning Page
                          </Button>
                        </Stack>
                      )}
                      <Button
                        onClick={() => setCancelJourney(j._id)}
                        variant="outlined"
                        fullWidth
                        color="error"
                        disabled={lockedJourneyStatuses.includes(j.status)}
                      >
                        Cancel Journey
                      </Button>
                      {!lockedJourneyStatuses.includes(j.status) && (
                        <CancelJourney
                          open={cancelJourney === j._id}
                          onClose={() => setCancelJourney('')}
                          journey={j}
                          actionAfterCancel={() => {
                            setCancelJourney('')
                            loadBooking()
                          }}
                        />
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                {j.assigned && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      Assigned Crew
                    </Typography>
                    {j.assigned?.length ? (
                      j.assigned.map((a, idx) => (
                        <Typography key={idx} variant="body2">
                          {a?.crewName || 'Unknown crew'}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No crew assigned.
                      </Typography>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No journeys found.
          </Typography>
        )}
      </Section>

      {/* Notes */}
      <Section title="Notes" icon={<Note fontSize="small" />}>
        {booking.bookingNotes}
      </Section>
      <Section title="Activity" icon={<Event fontSize="small" />}>
        {booking.notes?.length ? (
          booking.notes.map((n) => (
            <Box key={n._id} sx={{ mb: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {n.note}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {moment(n.createdAt).format('DD/MM/YYYY HH:mm')} -{' '}
                {n.author.firstName} {n.author.lastName}
              </Typography>
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No notes available.
          </Typography>
        )}
      </Section>
    </Stack>
  )
}

export default SingleBooking
